import clsx from 'clsx'
import React from 'react'
import { Control, Controller, UseFormRegisterReturn } from 'react-hook-form'
import { AiOutlineLoading3Quarters } from 'react-icons/ai'
import { FiUploadCloud } from 'react-icons/fi'
import { LuCalendarDays } from 'react-icons/lu'
import { getReactPickerDate } from '../../../../models/interface.helper'
import { Card, CardContent } from '../../../ui/card'
import ImageWrapper from './ImageWrapper'
import SpecialToggleDatePicker from './components/SpecialToggleDatePicker'

interface BaseNumberCardProps {
  /** The icon to display in the card */
  icon?: string
  /** Label for regular numbers (not 'other' numbers) */
  label?: string
  /** Whether the card is in saved (view) or edit mode */
  saved: boolean
  /** Handler for when the entire card is removed (for custom fields) */
  onRemove?: () => void
  /** Handler for image upload */
  onImageUpload: (file: File) => Promise<void>
  /** Handler for image removal */
  onImageRemove: () => Promise<void>
  /** Current image URL */
  imageUrl?: string
  /** Loading state passed from the parent for image upload / removal */
  isLoading: boolean
  /** Title field input props from react-hook-form register */
  titleField?: UseFormRegisterReturn<any>
  /** Number field input props from react-hook-form register */
  numberField: UseFormRegisterReturn<any>
  /** Form control to use with the date field */
  control: Control<any>
  /** Date field name to use with controller */
  dateFieldName: string
  /** Placeholder for title field */
  titlePlaceholder?: string
  /** Placeholder for number field */
  numberPlaceholder?: string
  /** Whether the card is used standalone (inside a modal or elsewhere) changes stylinging */
  standaloneCard?: boolean
}

const BaseNumberCard = ({
  icon,
  label,
  saved,
  onRemove,
  onImageUpload,
  onImageRemove,
  imageUrl,
  isLoading,
  titleField,
  numberField,
  control,
  dateFieldName,
  titlePlaceholder = 'Enter name',
  numberPlaceholder = 'Enter number',
  standaloneCard = false,
}: BaseNumberCardProps) => {
  const cardClassName = clsx(
    'rounded-lg relative w-full shadow-none',
    standaloneCard
      ? '!border-0 h-auto sm:h-[120px] w-full relative shadow-none sm:min-w-[360px] max-w-[400px]'
      : 'border-2 border-gray-100'
  )

  const contentSectionClassName = `flex-1 px-0  min-w-0 sm:px-2 py-0 flex flex-col  items-center text-center sm:items-start sm:text-left ${!standaloneCard ? '' : 'sm:ml-4'}`

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && onImageUpload) {
      await onImageUpload(file)
    }
  }

  // container class for all image containers
  const imageContainerClass = 'h-20 w-20 sm:h-20 sm:w-20 md:h-22 md:w-22'

  const renderImageSection = () => {
    // Loading state overlay
    if (isLoading) {
      return (
        <div className="relative">
          <div
            className={`${imageContainerClass} bg-[#f8f8f8] rounded-lg flex items-center justify-center overflow-hidden border-2 border-[#f1f2f2]`}
          >
            {imageUrl && (
              <img
                className="absolute w-full h-full object-cover opacity-40"
                alt={'Membership document'}
                src={imageUrl}
              />
            )}
            <div className="z-10 flex items-center justify-center h-full w-full">
              <AiOutlineLoading3Quarters className="animate-spin h-8 w-8 text-blue-500" />
            </div>
          </div>
        </div>
      )
    }

    // No image and not in saved mode - show upload box
    if (!imageUrl && !saved) {
      return (
        <div className="relative">
          <div
            className={`${imageContainerClass} bg-[#f8f8f8] rounded-lg flex items-center justify-center overflow-hidden`}
          >
            <div className="flex flex-col items-center justify-center h-full w-full">
              <div className="mb-1">
                <FiUploadCloud className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-gray-800" />
              </div>
              <div className="flex flex-col items-center text-center text-SeabiscuitDark200ThemeColor text-xs  font-normal">
                <p>Upload</p>
                <p>Document</p>
              </div>
            </div>
          </div>
          <input
            type="file"
            name="image"
            accept=".jpeg, .png, .jpg"
            onChange={handleFileChange}
            disabled={isLoading}
            className="absolute h-full w-full opacity-0 left-0 top-0 cursor-pointer z-10"
          />
        </div>
      )
    }

    // Image with edit mode
    if (!saved) {
      return (
        <ImageWrapper
          url={imageUrl || ''}
          removeHandler={onImageRemove}
          editMode={true}
          imageContainerClass={imageContainerClass}
        />
      )
    }

    // Image in view mode
    return (
      <ImageWrapper
        url={imageUrl || ''}
        removeHandler={() => {}}
        imageContainerClass={imageContainerClass}
      />
    )
  }

  return (
    <Card className={cardClassName}>
      {onRemove && !saved && (
        <button
          onClick={onRemove}
          className="absolute -top-3 -right-3 z-10 h-7 w-7 flex items-center justify-center bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors border border-gray-200"
        >
          <img
            src="/assets/og_icons/Cancel.svg"
            className="h-5 w-5"
            alt="Remove card"
            style={{
              filter:
                'invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)',
            }}
          />
        </button>
      )}

      <CardContent className="p-2 sm:p-4 h-full">
        <div className="flex flex-col sm:flex-row h-full gap-4 sm:gap-0 items-center px-6 py-4 sm:px-0 sm:py-0  ">
          {/* Icon section */}
          <div className="flex  justify-center h-full sm:w-[20%] mb-2 sm:mb-0  ">
            <div className="w-12 h-12 flex items-center justify-center">
              <img
                src={icon ?? '/assets/images/logos/custom-number-logo.png'}
                className={`object-contain ${!icon ? 'w-full h-full' : 'max-w-full max-h-full'}`}
                alt="Card icon"
              />
            </div>
          </div>

          {/* Content section */}
          <div className={contentSectionClassName}>
            {/* Title field */}
            {label ? (
              <p className="text-base sm:text-lg whitespace-nowrap font-semibold">{label}</p>
            ) : titleField ? (
              <div className="relative">
                <input
                  type="text"
                  {...titleField}
                  placeholder={titlePlaceholder}
                  disabled={saved}
                  className="bg-transparent !text-base sm:!text-lg text-gray-800 placeholder:text-base placeholder:text-gray-800 font-semibold focus:ring-0 border-0 outline-none w-full pb-0 pl-0 truncate"
                />
                {!saved && (
                  <div className="absolute bottom-0 left-0 h-[1px] w-full max-w-[120px] bg-gray-600"></div>
                )}
              </div>
            ) : (
              <p className="text-base sm:text-lg font-semibold">Number Information</p>
            )}

            {/* Number field */}
            <div className="relative flex flex-col items-center sm:items-start">
              <input
                type="text"
                {...numberField}
                placeholder={numberPlaceholder}
                disabled={saved}
                className="bg-transparent !text-base sm:!text-md text-gray-800 placeholder:text-gray-800 font-normal focus:ring-0 border-0 outline-none w-full pb-0 pl-0 text-center sm:text-left"
              />
              {!saved && (
                <div className="h-[1px]  bg-gray-600 sm:absolute sm:bottom-0 sm:left-0 sm:w-full sm:max-w-[100px]"></div>
              )}
            </div>

            {/* Date field */}
            <div className="flex items-center mt-2">
              <span className="flex-shrink-0">
                <LuCalendarDays className="text-gray-700 mr-2" size={16} />
              </span>
              <div className="relative w-full max-w-[160px] sm:max-w-[180px] md:max-w-[200px] lg:max-w-[150px]  xl:max-w-[180px]">
                <Controller
                  name={dateFieldName}
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <SpecialToggleDatePicker
                      onChange={onChange}
                      onBlur={onBlur}
                      showToggle={true}
                      toggleLabel="Lifetime member"
                      specialDateText="Lifetime member"
                      selected={getReactPickerDate(value)}
                      placeholderText="Expiration date"
                      disabled={saved}
                      className="text-sm sm:text-sm w-full"
                    />
                  )}
                />
                {!saved && (
                  <div className="absolute bottom-0 left-0 h-[1px] w-[110px] bg-gray-600"></div>
                )}
              </div>
            </div>
          </div>

          {/* Image section */}
          <div className="flex sm:flex-none sm:w-[30%] justify-center sm:justify-end items-center mt-2 sm:mt-0 ">
            {renderImageSection()}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default BaseNumberCard
